import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useCompanyUsers } from "@/lib/useCompanyUsers";
import { UserProfile } from "@/lib/stores/storeTypes";
import apiService from "@/lib/api-functions/api-service";
import { locanestApiService } from "@/lib/api-functions/locanest-api-service";
import {
  getUserRoleInCompany,
  isCompanyOwner,
  getCurrentUser,
  unifiedUserStore,
} from "@/lib/stores/unifiedUserStore";
import {
  UserManagementUtils,
  ExtendedUser,
  UserGameAccess,
  createUser,
  updateUserRole,
  updateUserGameAccess,
  formatLastLogin,
  transformUserToExtended,
  canRemoveUser,
  removeUser,
  validateEmail,
} from "@/lib/utils/userManagementUtils";
import SaveBar from "./SaveBar";
import { Button } from   "@/components/ui/button";
import { Badge } from   "@/components/ui/badge";
import { Trash2, UserPlus, Shield, ShieldCheck, Eye } from "lucide-react";
import { getCompanyRoles, hasPermission } from "@/lib/utils/permissions";
import type { CompanyRole } from "@/lib/stores/storeTypes";
import ConfirmExistingUserDialog from   "@/components/ui/dialogs/ConfirmExistingUserDialog";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
type UserManagementSectionProps = Record<string, never>;

export default function UserManagementSection(
  _props: UserManagementSectionProps
) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { selectedCompany, games, refreshData } = useSimplifiedUserData();
  const {
    users: companyUsers,
    loading,
    error,
    refresh,
  } = useCompanyUsers({ companyId: selectedCompany?.id, autoFetch: true });

  // Check if current user is a company owner
  const currentUser = getCurrentUser();
  const isCurrentUserOwner =
    currentUser && selectedCompany
      ? isCompanyOwner(selectedCompany.id, currentUser.id)
      : false;
  
  // Get available roles for this company
  const availableRoles = selectedCompany ? getCompanyRoles(selectedCompany.id) : [];
  const defaultRole = availableRoles.find(r => r.role_name.toLowerCase() === 'contributor')?.role_name || 'contributor';
  
  // Check permissions
  const canInviteUsers = selectedCompany ? hasPermission(selectedCompany.id, 'users.invite') : false;
  const canManageUsers = selectedCompany ? hasPermission(selectedCompany.id, 'users.manage') : false;
  const canRemoveUsers = selectedCompany ? hasPermission(selectedCompany.id, 'users.remove') : false;
  
  // Filter games to only those from the selected company (memoized for performance)
  const companyGames = useMemo(() => {
    return selectedCompany 
      ? games.filter(game => game.company_id === selectedCompany.id)
      : [];
  }, [selectedCompany, games]);

  // State for user management
  const [users, setUsers] = useState<ExtendedUser[]>([]);
  const [originalUsers, setOriginalUsers] = useState<ExtendedUser[]>([]);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState<string>(defaultRole);
  const [newUserGameIds, setNewUserGameIds] = useState<string[]>([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [removingUsers, setRemovingUsers] = useState<Set<string>>(new Set());
  const [savingGameAccess, setSavingGameAccess] = useState<Set<string>>(new Set()); // Track which user-game pairs are being saved
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [removingInvitations, setRemovingInvitations] = useState<Set<string>>(new Set());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [lookedUpUser, setLookedUpUser] = useState<{
    id: string;
    email: string;
    username: string;
    full_name: string;
  } | null>(null);
  const [isInviting, setIsInviting] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailCheckResult, setEmailCheckResult] = useState<{
    exists: boolean;
    user?: {
      id: string;
      email: string;
      username: string;
      full_name: string;
    };
  } | null>(null);
  
  // State for actual game memberships (from company_members_games table)
  const [actualGameMemberships, setActualGameMemberships] = useState<Record<string, string[]>>({});
  const [loadingMemberships, setLoadingMemberships] = useState(false);

  // Fetch actual game memberships when company changes
  useEffect(() => {
    const fetchGameMemberships = async () => {
      if (!selectedCompany?.id) return;
      
      setLoadingMemberships(true);
      try {
        const response = await apiService.fetchGameMemberships(selectedCompany.id);
        if (response.success && response.data) {
          setActualGameMemberships(response.data);
        }
      } catch (error) {
        console.error('Error fetching game memberships:', error);
      } finally {
        setLoadingMemberships(false);
      }
    };

    fetchGameMemberships();
  }, [selectedCompany?.id]);

  // Custom transform function that uses actual game memberships
  const transformUserWithActualAccess = (
    user: UserProfile,
    companyId: string,
    games: Array<{ id: string; name: string }>,
    userRole: string,
    gameMemberships: Record<string, string[]>
  ): ExtendedUser => {
    return {
      ...user,
      role: userRole,
      company_id: companyId,
      gameAccess: games.map(game => ({
        gameId: game.id,
        gameName: game.name,
        // Use actual game memberships instead of game.member_ids
        hasAccess: gameMemberships[game.id]?.includes(user.id) || false
      })),
      lastLogin: user.updated_at
    };
  };

  // Initialize users data with actual game memberships
  useEffect(() => {
    if (companyUsers && companyGames.length > 0 && selectedCompany && !loadingMemberships) {
      const extendedUsers = companyUsers.map((user) => {
        // Get the actual role from company data
        const userRole =
          getUserRoleInCompany(selectedCompany.id, user.id) || "contributor";

        return transformUserWithActualAccess(
          user,
          selectedCompany.id,
          companyGames,
          userRole,
          actualGameMemberships
        );
      });

      setUsers(extendedUsers);
      setOriginalUsers(JSON.parse(JSON.stringify(extendedUsers)));
    }
  }, [companyUsers, companyGames, selectedCompany, actualGameMemberships, loadingMemberships]);

  // Load pending invitations
  useEffect(() => {
    const loadPendingInvitations = async () => {
      if (!selectedCompany?.id || !canInviteUsers) return;
      
      setLoadingPending(true);
      try {
        const response = await apiService.getPendingInvitations(selectedCompany.id);
        if (response.success && response.data) {
          setPendingInvitations(response.data);
        }
      } catch (error) {
        console.error('Error loading pending invitations:', error);
      } finally {
        setLoadingPending(false);
      }
    };

    loadPendingInvitations();
  }, [selectedCompany?.id, canInviteUsers]);

  // Check if there are any changes
  const hasChanges =
    JSON.stringify(users) !== JSON.stringify(originalUsers) || showInviteForm;

  // Handle role change
  const handleRoleChange = (
    userId: string,
    newRole: string
  ) => {
    setUsers((prev) => updateUserRole(prev, userId, newRole));
  };

  // Handle game access change - saves immediately to backend
  const handleGameAccessChange = async (
    userId: string,
    gameId: string,
    hasAccess: boolean
  ) => {
    if (!selectedCompany?.id) return;

    const accessKey = `${userId}-${gameId}`;
    
    // Prevent multiple saves for the same checkbox
    if (savingGameAccess.has(accessKey)) return;

    // First update local state optimistically
    const user = users.find(u => u.id === userId);
    if (!user) return;

    // Check if user is an owner (owners can't have individual game access modified)
    if (user.role.toLowerCase() === "owner") {
      toast({
        title: "Cannot modify access",
        description: "Owners have access to all games by default",
        variant: "destructive",
      });
      return;
    }

    // Update local state optimistically
    const newGameAccess = user.gameAccess.map(access =>
      access.gameId === gameId ? { ...access, hasAccess } : access
    );

    setUsers((prev) =>
      prev.map(u =>
        u.id === userId ? { ...u, gameAccess: newGameAccess } : u
      )
    );

    // Mark as saving
    setSavingGameAccess(prev => new Set(prev).add(accessKey));

    // Save to backend immediately
    try {
      const result = await UserManagementUtils.updateUser({
        userId,
        companyId: selectedCompany.id,
        gameAccess: newGameAccess,
      });

      if (result.success) {
        // Update originalUsers to reflect the saved state
        setOriginalUsers((prev) =>
          prev.map(u =>
            u.id === userId ? { ...u, gameAccess: newGameAccess } : u
          )
        );

        // Update actualGameMemberships to reflect the change
        setActualGameMemberships((prev) => {
          const newMemberships = { ...prev };
          if (!newMemberships[gameId]) {
            newMemberships[gameId] = [];
          }
          if (hasAccess) {
            // Add user to game memberships if not already there
            if (!newMemberships[gameId].includes(userId)) {
              newMemberships[gameId] = [...newMemberships[gameId], userId];
            }
          } else {
            // Remove user from game memberships
            newMemberships[gameId] = newMemberships[gameId].filter(id => id !== userId);
          }
          return newMemberships;
        });

        // Refresh data to ensure consistency
        await refreshData();

        const gameName = user.gameAccess.find(g => g.gameId === gameId)?.gameName || 'game';
        toast({
          title: hasAccess ? "Access granted" : "Access removed",
          description: `${user.username || user.email} ${hasAccess ? 'now has access to' : 'no longer has access to'} ${gameName}`,
        });
      } else {
        // Revert local state on failure
        setUsers((prev) =>
          prev.map(u =>
            u.id === userId ? { ...u, gameAccess: user.gameAccess } : u
          )
        );

        toast({
          title: "Failed to update access",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      // Revert local state on error
      setUsers((prev) =>
        prev.map(u =>
          u.id === userId ? { ...u, gameAccess: user.gameAccess } : u
        )
      );

      console.error("Error updating game access:", error);
      toast({
        title: "Error",
        description: "Failed to update game access. Please try again.",
        variant: "destructive",
      });
    } finally {
      // Remove saving state
      setSavingGameAccess(prev => {
        const newSet = new Set(prev);
        newSet.delete(accessKey);
        return newSet;
      });
    }
  };

  // Handle user removal
  const handleRemoveUser = async (userId: string) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;

    if (!selectedCompany?.id) {
      toast({
        title: "Error",
        description: "No company selected",
        variant: "destructive",
      });
      return;
    }

    const { canRemove, reason } = canRemoveUser(user, currentUser?.id);

    if (!canRemove) {
      toast({
        title: "Cannot remove user",
        description: reason,
        variant: "destructive",
      });
      return;
    }

    // Add to removing set to show loading state
    setRemovingUsers((prev) => new Set(prev).add(userId));

    try {
      // Call the API to actually remove the user
      const result = await removeUser(userId, selectedCompany.id, currentUser);

      if (result.success) {
        // Remove from local state only if API call succeeded
        setUsers((prev) => prev.filter((u) => u.id !== userId));

        // Also update originalUsers to reflect the change
        setOriginalUsers((prev) => prev.filter((u) => u.id !== userId));

        toast({
          title: "Success",
          description: result.message,
        });
      } else {
        toast({
          title: "Failed to remove user",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error removing user:", error);
      toast({
        title: "Error",
        description: "Failed to remove user. Please try again.",
        variant: "destructive",
      });
    } finally {
      // Remove from removing set
      setRemovingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Check if email exists when user leaves the email field
  const handleEmailBlur = async () => {
    if (!newUserEmail || !newUserEmail.includes('@')) {
      setEmailCheckResult(null);
      return;
    }

    setIsCheckingEmail(true);
    try {
      const lookupResult = await locanestApiService.lookupUserByEmail(newUserEmail);
      if (lookupResult.success) {
        setEmailCheckResult({
          exists: lookupResult.exists,
          user: lookupResult.user
        });
      } else {
        setEmailCheckResult(null);
      }
    } catch (error) {
      console.error("Error looking up user:", error);
      setEmailCheckResult(null);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  // Handle adding new user - first lookup, then show confirmation if exists
  const handleAddUser = async () => {
    if (!selectedCompany?.id) {
      toast({
        title: "No company selected",
        description: "Please select a company first",
        variant: "destructive",
      });
      return;
    }

    if (newUserGameIds.length === 0) {
      toast({
        title: "No games selected",
        description: "Please select at least one game for the user",
        variant: "destructive",
      });
      return;
    }

    // If we already have email check result showing user exists, use it
    if (emailCheckResult?.exists && emailCheckResult.user) {
      setLookedUpUser(emailCheckResult.user);
      setShowConfirmDialog(true);
    } else {
      // Otherwise proceed with normal invitation
      await performInvite();
    }
  };

  // Perform the actual invitation after confirmation
  const performInvite = async () => {
    if (!selectedCompany?.id) return;

    setIsInviting(true);
    try {
      const result = await createUser(
        {
          email: newUserEmail,
          settings: UserManagementUtils.getDefaultUserSettings(),
          role: newUserRole,
          companyId: selectedCompany.id,
          gameIds: newUserGameIds,
        },
        users
      );

      if (result.success) {
        // Refresh the user context to include the new user
        await refreshData();

        // Reload pending invitations
        const response = await apiService.getPendingInvitations(selectedCompany.id);
        if (response.success && response.data) {
          setPendingInvitations(response.data);
        }

        setNewUserEmail("");
        setNewUserRole(defaultRole);
        setNewUserGameIds([]);
        setShowInviteForm(false);
        setShowConfirmDialog(false);
        setLookedUpUser(null);
        setEmailCheckResult(null);

        // Show appropriate message based on response
        const data = result.data as any;
        let description = result.message;
        
        if (data?.user_existed) {
          description = `User ${data.email} already existed and has been added to the company.`;
        } else if (data?.email_sent) {
          description = `Invitation email sent to ${data.email}. They will appear here once they accept.`;
        } else if (data?.warning) {
          description = data.warning;
        }

        toast({
          title: data?.user_existed ? "User Added" : "Invitation Sent",
          description: description,
        });
      } else {
        toast({
          title: result.error || "Invitation failed",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error inviting user:", error);
      toast({
        title: "Error",
        description: "Failed to invite user. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsInviting(false);
    }
  };

  // Handle save changes
  const handleSave = async () => {
    if (!selectedCompany?.id) {
      toast({
        title: "Error",
        description: "No company selected",
        variant: "destructive",
      });
      return;
    }

    try {
      // Calculate changes using utility function
      const changes = UserManagementUtils.calculateUserChanges(
        originalUsers,
        users
      );

      console.log("Saving user management changes:", {
        originalUsers,
        updatedUsers: users,
        ...changes,
      });

      let hasErrors = false;
      const results: string[] = [];

      // 1. Handle role changes
      for (const user of changes.roleChanges) {
        const result = await UserManagementUtils.updateUser({
          userId: user.id,
          companyId: selectedCompany.id,
          role: user.role,
          gameAccess: user.gameAccess,
        });

        if (result.success) {
          results.push(`Updated role for ${user.username || user.email}`);
        } else {
          hasErrors = true;
          results.push(
            `Failed to update ${user.username || user.email}: ${result.message}`
          );
        }
      }

      // 2. Handle game access changes
      for (const user of changes.accessChanges) {
        // Only update game access if role wasn't already changed (to avoid duplicate updates)
        if (!changes.roleChanges.find((ru) => ru.id === user.id)) {
          const result = await UserManagementUtils.updateUser({
            userId: user.id,
            companyId: selectedCompany.id,
            gameAccess: user.gameAccess,
          });

          if (result.success) {
            results.push(
              `Updated game access for ${user.username || user.email}`
            );
          } else {
            hasErrors = true;
            results.push(
              `Failed to update game access for ${
                user.username || user.email
              }: ${result.message}`
            );
          }
        }
      }

      // 3. Handle user removals
      for (const user of changes.removedUsers) {
        const result = await UserManagementUtils.removeUser(
          user.id,
          selectedCompany.id,
          currentUser
        );

        if (result.success) {
          results.push(`Removed ${user.username || user.email} from company`);
        } else {
          hasErrors = true;
          results.push(
            `Failed to remove ${user.username || user.email}: ${result.message}`
          );
        }
      }

      // Update original users to match current state
      setOriginalUsers(JSON.parse(JSON.stringify(users)));

      // Refresh user context data from server
      await refreshData();

      // Show results
      if (hasErrors) {
        toast({
          title: "Partial Success",
          description: `Some operations completed with errors. Check console for details.`,
          variant: "destructive",
        });
        console.log("Save results:", results);
      } else {
        toast({
          title: "Success",
          description: `User management changes saved successfully. ${results.length} operations completed.`,
        });
      }
    } catch (error) {
      console.error("Error saving user management changes:", error);
      toast({
        title: "Error",
        description: "Failed to save user management changes",
        variant: "destructive",
      });
    }
  };

  // Handle cancel changes
  const handleCancel = () => {
    setUsers(JSON.parse(JSON.stringify(originalUsers)));
    setNewUserEmail("");
    setNewUserRole(defaultRole);
    setNewUserGameIds([]);
    setShowInviteForm(false);
    setEmailCheckResult(null);
  };

  // Handle removing pending invitation
  const handleRemoveInvitation = async (invitationId: string, targetEmail: string) => {
    if (!selectedCompany?.id) {
      toast({
        title: "Error",
        description: "No company selected",
        variant: "destructive",
      });
      return;
    }

    // Add to removing set to show loading state
    setRemovingInvitations((prev) => new Set(prev).add(invitationId));

    try {
      // Call the API to revoke the invitation code
      const result = await apiService.revokeInvitationCode(invitationId);

      if (result.success) {
        // Remove from local state
        setPendingInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));

        toast({
          title: "Invitation removed",
          description: `Invitation for ${targetEmail} has been removed`,
        });
      } else {
        toast({
          title: "Failed to remove invitation",
          description: result.message || "An error occurred",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error removing invitation:", error);
      toast({
        title: "Error",
        description: "Failed to remove invitation. Please try again.",
        variant: "destructive",
      });
    } finally {
      // Remove from removing set
      setRemovingInvitations((prev) => {
        const newSet = new Set(prev);
        newSet.delete(invitationId);
        return newSet;
      });
    }
  };

  if (loading || loadingMemberships) {
    return (
      <div className="max-w-4xl space-y-6">
        <div className="text-center py-8 text-muted-foreground">
          <p>{t('platform.settings.usermanagementsection.loading_users')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl space-y-6">
        <div className="text-center py-8 text-destructive">
          <p>Error loading users: {error}</p>
          <Button onClick={() => refresh()} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-4xl space-y-6">
        <div className="flex justify-between items-center">
          {canInviteUsers && (
            <Button onClick={() => setShowInviteForm(true)}>
              <UserPlus className="h-4 w-4" />
              Invite User
            </Button>
          )}
          {!canInviteUsers && (
            <div className="text-sm text-muted-foreground">
              You need user invitation permissions to invite users
            </div>
          )}
        </div>

        {selectedCompany && (
          <p className="text-muted-foreground">
            Managing users for{" "}
            <span className="font-medium">{selectedCompany.name}</span>
          </p>
        )}

        {/* Invite New User Form */}
        {showInviteForm && (
          <div className="bg-muted p-6 rounded border">
            <h3 className="text-lg font-medium text-foreground mb-4">
              Invite New User
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label
                  htmlFor="new-user-email"
                  className="block text-sm font-medium text-muted-foreground mb-2"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="new-user-email"
                  dir="auto"
                  value={newUserEmail}
                  onChange={(e) => {
                    setNewUserEmail(e.target.value);
                    setEmailCheckResult(null); // Clear check result when email changes
                  }}
                  onBlur={handleEmailBlur}
                  placeholder={t('platform.settings.usermanagementsection.userexamplecom')}
                  className="w-full px-3 py-2 bg-background text-foreground border border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                
                {/* Email check status */}
                {isCheckingEmail && (
                  <div className="mt-2 flex items-center text-sm text-muted-foreground">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2"></div>
                    Checking if user exists...
                  </div>
                )}
                
                {!isCheckingEmail && emailCheckResult?.exists && emailCheckResult.user && (
                  <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                    <div className="flex items-start space-x-2">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                        <span className="text-sm font-semibold text-blue-600 dark:text-blue-300">
                          {emailCheckResult.user.full_name?.charAt(0).toUpperCase() || emailCheckResult.user.username?.charAt(0).toUpperCase() || emailCheckResult.user.email.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                          User found: {emailCheckResult.user.full_name || 'No name set'}
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          @{emailCheckResult.user.username}
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          This user will be added directly to your company
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {!isCheckingEmail && emailCheckResult?.exists === false && (
                  <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                    <p className="text-xs text-green-800 dark:text-green-200">
                      New user - an invitation email will be sent
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label
                  htmlFor="new-user-role"
                  className="block text-sm font-medium text-muted-foreground mb-2"
                >
                  Role
                </label>
                <select
                  id="new-user-role"
                  value={newUserRole}
                  onChange={(e) =>
                    setNewUserRole(e.target.value)
                  }
                  className="w-full px-3 py-2 bg-background text-foreground border border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {availableRoles.map((role) => (
                    <option key={role.id} value={role.role_name}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Game Access Selection */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Game Access *
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-input rounded p-3 bg-background">
                {companyGames.length === 0 ? (
                  <p className="text-sm text-muted-foreground col-span-2">
                    No games available in this company
                  </p>
                ) : (
                  companyGames.map((game) => (
                    <label
                      key={game.id}
                      className="flex items-center space-x-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={newUserGameIds.includes(game.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewUserGameIds([...newUserGameIds, game.id]);
                          } else {
                            setNewUserGameIds(
                              newUserGameIds.filter((id) => id !== game.id)
                            );
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded"
                      />
                      <span className="text-sm text-foreground">{game.name}</span>
                    </label>
                  ))
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Select which games this user will have access to
              </p>
            </div>

            <div className="flex justify-end space-x-3 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowInviteForm(false);
                  setNewUserEmail("");
                  setNewUserRole(defaultRole);
                  setNewUserGameIds([]);
                  setEmailCheckResult(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleAddUser}>{t('platform.settings.usermanagementsection.send_invitation')}</Button>
            </div>
          </div>
        )}

        {/* Pending Invitations */}
        {isCurrentUserOwner && pendingInvitations.length > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded overflow-hidden">
            <div className="px-6 py-4 border-b bg-yellow-100 dark:bg-yellow-900/30">
              <h3 className="text-lg font-medium text-foreground">
                Pending Invitations ({pendingInvitations.length})
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Users who have been invited but haven't accepted yet
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-yellow-50 dark:bg-yellow-900/20">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Games
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Invited By
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Invited On
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-background divide-y divide-border">
                  {pendingInvitations.map((invitation) => {
                    // Ensure game_ids is an array
                    const gameIds = Array.isArray(invitation.game_ids) 
                      ? invitation.game_ids 
                      : (typeof invitation.game_ids === 'string' 
                        ? JSON.parse(invitation.game_ids) 
                        : []);
                    
                    const invitedGames = gameIds
                      .map((id: string) => companyGames.find(g => g.id === id)?.name)
                      .filter(Boolean)
                      .join(", ");
                    
                    return (
                      <tr key={invitation.id} className="hover:bg-yellow-50 dark:hover:bg-yellow-900/20">
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-foreground">
                            {invitation.target_email}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Code: {invitation.code}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-foreground capitalize">
                            {invitation.role}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-muted-foreground truncate max-w-xs block" title={invitedGames}>
                            {invitedGames || "No games"}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-muted-foreground">
                            {invitation.created_by_name || "Unknown"}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-muted-foreground">
                            {new Date(invitation.created_at).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200">
                            Pending
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveInvitation(invitation.id, invitation.target_email)}
                            className="text-red-600 hover:text-red-900 h-8 w-8 p-0"
                            disabled={removingInvitations.has(invitation.id)}
                            title="Remove invitation"
                          >
                            {removingInvitations.has(invitation.id) ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                            ) : (
                              <Trash2 className="h-3 w-3" />
                            )}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Users List */}
        <div className="bg-background border rounded overflow-hidden">
          <div className="px-6 py-4 border-b bg-surface">
            <h3 className="text-lg font-medium text-muted-foreground">
              Company Members ({users.length})
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-input">
              <thead className="bg-surface">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-1/4">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-1/6">
                    Role & Login
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-2/5">
                    Game Access
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-1/6">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-background divide-y divide-border">
                {users.map((user) => (
                  <tr key={user.id} className="">
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                            <span className="text-xs font-medium text-foreground">
                              {(user.username || user.email)
                                .charAt(0)
                                .toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-foreground">
                            {user.username || user.email.split("@")[0]}
                          </div>
                          <div className="text-xs text-muted-foreground truncate max-w-32">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="space-y-2">
                        <select
                          value={
                            // Find the matching role from availableRoles (case-insensitive)
                            availableRoles.find(r => r.role_name.toLowerCase() === user.role.toLowerCase())?.role_name || user.role
                          }
                          onChange={(e) =>
                            handleRoleChange(
                              user.id,
                              e.target.value
                            )
                          }
                          className="text-xs bg-background text-foreground border border-input rounded px-2 py-1 focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={!canManageUsers} // Only users with manage permission can change roles
                        >
                          {availableRoles.map((role) => (
                            <option key={role.id} value={role.role_name}>
                              {role.role_name}
                            </option>
                          ))}
                        </select>
                        <div className="text-xs text-muted-foreground">
                          {formatLastLogin(user.lastLogin)}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        {user.role.toLowerCase() === "owner" ? (
                          <Badge variant="secondary" className="text-xs">
                            All Games Access
                          </Badge>
                        ) : (
                          <div className="grid grid-cols-2 gap-1">
                            {user.gameAccess.map((access) => {
                              const isSaving = savingGameAccess.has(`${user.id}-${access.gameId}`);
                              return (
                                <label
                                  key={access.gameId}
                                  className={`flex items-center space-x-1 text-xs ${isSaving ? 'opacity-60' : ''}`}
                                >
                                  {isSaving ? (
                                    <div className="h-3 w-3 flex items-center justify-center">
                                      <div className="animate-spin rounded-full h-2.5 w-2.5 border-b-2 border-blue-600"></div>
                                    </div>
                                  ) : (
                                    <input
                                      type="checkbox"
                                      checked={access.hasAccess}
                                      onChange={(e) =>
                                        handleGameAccessChange(
                                          user.id,
                                          access.gameId,
                                          e.target.checked
                                        )
                                      }
                                      disabled={!canManageUsers}
                                      className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-input rounded cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                    />
                                  )}
                                  <span
                                    className="truncate max-w-16 text-foreground"
                                    title={access.gameName}
                                  >
                                    {access.gameName}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveUser(user.id)}
                          className="text-red-600 hover:text-red-900 h-8 w-8 p-0"
                          disabled={
                            !canRemoveUsers ||
                            user.role.toLowerCase() === "owner" ||
                            removingUsers.has(user.id)
                          }
                          title={
                            removingUsers.has(user.id)
                              ? "Removing user..."
                              : !isCurrentUserOwner
                              ? "You need user removal permissions"
                              : user.role.toLowerCase() === "owner"
                              ? "Cannot remove company owners"
                              : "Remove user"
                          }
                        >
                          {removingUsers.has(user.id) ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {users.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p>{t('platform.settings.usermanagementsection.no_users_found_invite_users_to_get_started')}</p>
          </div>
        )}
      </div>

      <SaveBar
        isVisible={hasChanges}
        onSave={handleSave}
        onCancel={handleCancel}
        hasChanges={hasChanges}
      />

      {/* Confirmation dialog for existing users */}
      {lookedUpUser && (
        <ConfirmExistingUserDialog
          isOpen={showConfirmDialog}
          onClose={() => {
            setShowConfirmDialog(false);
            setLookedUpUser(null);
          }}
          onConfirm={performInvite}
          user={lookedUpUser}
          isSubmitting={isInviting}
        />
      )}
    </div>
  );
}
